import { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Calculator, Wallet, Receipt, Plus, Trash, PiggyBank, TrendUp, TrendDown, Minus, ArrowDown, ArrowUp, Equals, CurrencyDollar, FloppyDisk, ClockCounterClockwise, CheckCircle
} from "@phosphor-icons/react";

const INCOME_CATEGORIES = ["Salary", "Freelance", "Investments", "Rental", "Side Hustle", "Other"];
const EXPENSE_CATEGORIES = ["Housing", "Utilities", "Food", "Transportation", "Insurance", "Healthcare", "Debt Payments", "Entertainment", "Subscriptions", "Savings/Investing", "Education", "Personal", "Other"];
const ASSET_TYPES = ["Cash / Savings", "Stocks / ETFs", "Crypto", "Real Estate", "Retirement (401k/IRA)", "Vehicle", "Business Equity", "Other"];
const LIABILITY_TYPES = ["Mortgage", "Car Loan", "Student Loans", "Credit Card", "Personal Loan", "Medical Debt", "Other"];

function fmtMoney(val) {
  return `$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function EntryRow({ entry, onUpdate, onRemove, categories, testPrefix }) {
  return (
    <div className="py-2 border-b border-zinc-800/30 last:border-0 group" data-testid={`${testPrefix}-row-${entry.id}`}>
      <div className="flex items-center gap-1.5">
        <select
          value={entry.category}
          onChange={e => onUpdate(entry.id, "category", e.target.value)}
          className="bg-zinc-950 border border-zinc-800 rounded-sm px-1.5 py-1.5 text-[10px] md:text-[11px] text-zinc-300 focus:outline-none focus:border-amber-500/50 w-24 md:w-36 shrink-0"
        >
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          type="text"
          value={entry.name}
          onChange={e => onUpdate(entry.id, "name", e.target.value)}
          placeholder="Description"
          className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-[10px] md:text-[11px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
        />
        <div className="relative shrink-0">
          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600">$</span>
          <input
            type="number"
            value={entry.amount || ""}
            onChange={e => onUpdate(entry.id, "amount", parseFloat(e.target.value) || 0)}
            placeholder="0"
            min="0"
            step="0.01"
            className="w-20 md:w-28 bg-zinc-950 border border-zinc-800 rounded-sm pl-4 pr-1 py-1.5 text-[10px] md:text-[11px] text-zinc-200 text-right font-data placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <button
          onClick={() => onRemove(entry.id)}
          className="text-zinc-700 hover:text-red-400 transition-colors md:opacity-0 md:group-hover:opacity-100 shrink-0"
        >
          <Trash size={12} />
        </button>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-2 md:p-4 text-center">
      <Icon size={14} className={`mx-auto mb-1 ${color}`} />
      <span className="text-[7px] md:text-[8px] uppercase tracking-wider text-zinc-500 block">{label}</span>
      <span className={`text-sm md:text-lg font-bold font-data block ${color}`}>{value}</span>
      {sub && <span className="text-[8px] md:text-[9px] text-zinc-600 block">{sub}</span>}
    </div>
  );
}

/* ========== BUDGET CALCULATOR ========== */
function BudgetCalculator({ incomes, setIncomes, expenses, setExpenses, nextId, setNextId }) {
  const addEntry = (setter) => {
    setter(prev => [...prev, { id: nextId, category: prev[0]?.category || "Other", name: "", amount: 0 }]);
    setNextId(n => n + 1);
  };

  const updateEntry = (setter) => (id, field, value) => {
    setter(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const removeEntry = (setter) => (id) => {
    setter(prev => prev.filter(e => e.id !== id));
  };

  const totalIncome = useMemo(() => incomes.reduce((s, e) => s + (e.amount || 0), 0), [incomes]);
  const totalExpenses = useMemo(() => expenses.reduce((s, e) => s + (e.amount || 0), 0), [expenses]);
  const netCashFlow = totalIncome - totalExpenses;
  const savingsRate = totalIncome > 0 ? ((netCashFlow / totalIncome) * 100) : 0;

  // Expense breakdown by category
  const expenseBreakdown = useMemo(() => {
    const map = {};
    expenses.forEach(e => {
      if (e.amount > 0) {
        map[e.category] = (map[e.category] || 0) + e.amount;
      }
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

  return (
    <div className="space-y-3 md:space-y-5" data-testid="budget-calculator">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3" data-testid="budget-summary">
        <SummaryCard label="Total Income" value={fmtMoney(totalIncome)} icon={ArrowDown} color="text-emerald-400" sub="/month" />
        <SummaryCard label="Total Expenses" value={fmtMoney(totalExpenses)} icon={ArrowUp} color="text-red-400" sub="/month" />
        <SummaryCard
          label="Net Cash Flow"
          value={`${netCashFlow >= 0 ? "" : "-"}${fmtMoney(netCashFlow)}`}
          icon={netCashFlow >= 0 ? TrendUp : TrendDown}
          color={netCashFlow >= 0 ? "text-emerald-400" : "text-red-400"}
          sub="/month"
        />
        <SummaryCard
          label="Savings Rate"
          value={`${savingsRate.toFixed(1)}%`}
          icon={PiggyBank}
          color={savingsRate >= 20 ? "text-emerald-400" : savingsRate >= 10 ? "text-amber-400" : "text-red-400"}
          sub={savingsRate >= 20 ? "Excellent" : savingsRate >= 10 ? "Good" : "Needs work"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5">
        {/* Income Section */}
        <div className="bg-zinc-900/40 border border-amber-500/15 rounded-sm p-3 md:p-4" data-testid="income-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ArrowDown size={14} className="text-emerald-400" />
              <span className="text-[11px] font-semibold text-zinc-200 uppercase tracking-wider">Income</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-emerald-900/40 text-emerald-400">{fmtMoney(totalIncome)}</Badge>
            </div>
            <button
              onClick={() => addEntry(setIncomes)}
              data-testid="add-income-btn"
              className="text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1 px-2 py-1 border border-emerald-800/40 rounded-sm hover:bg-emerald-900/20"
            >
              <Plus size={10} weight="bold" /> Add
            </button>
          </div>
          <div className="space-y-0.5">
            {incomes.map(e => (
              <EntryRow key={e.id} entry={e} onUpdate={updateEntry(setIncomes)} onRemove={removeEntry(setIncomes)} categories={INCOME_CATEGORIES} testPrefix="income" />
            ))}
          </div>
        </div>

        {/* Expenses Section */}
        <div className="bg-zinc-900/40 border border-amber-500/15 rounded-sm p-3 md:p-4" data-testid="expense-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ArrowUp size={14} className="text-red-400" />
              <span className="text-[11px] font-semibold text-zinc-200 uppercase tracking-wider">Expenses</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-red-900/40 text-red-400">{fmtMoney(totalExpenses)}</Badge>
            </div>
            <button
              onClick={() => addEntry(setExpenses)}
              data-testid="add-expense-btn"
              className="text-[10px] text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 px-2 py-1 border border-red-800/40 rounded-sm hover:bg-red-900/20"
            >
              <Plus size={10} weight="bold" /> Add
            </button>
          </div>
          <div className="space-y-0.5">
            {expenses.map(e => (
              <EntryRow key={e.id} entry={e} onUpdate={updateEntry(setExpenses)} onRemove={removeEntry(setExpenses)} categories={EXPENSE_CATEGORIES} testPrefix="expense" />
            ))}
          </div>
        </div>
      </div>

      {/* Expense Breakdown */}
      {expenseBreakdown.length > 0 && (
        <div className="bg-zinc-900/30 border border-amber-500/10 rounded-sm p-3 md:p-4" data-testid="expense-breakdown">
          <span className="text-[9px] md:text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block mb-2 md:mb-3">Expense Breakdown</span>
          <div className="space-y-1.5 md:space-y-2">
            {expenseBreakdown.map(([cat, amount]) => {
              const pct = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0;
              return (
                <div key={cat} className="flex items-center gap-2 md:gap-3">
                  <span className="text-[10px] md:text-[11px] text-zinc-400 w-24 md:w-32 shrink-0 truncate">{cat}</span>
                  <div className="flex-1 h-2.5 md:h-3 bg-zinc-800/60 rounded-sm overflow-hidden">
                    <div className="h-full bg-amber-500/40 rounded-sm transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[9px] md:text-[10px] font-data text-zinc-300 w-16 md:w-20 text-right">{fmtMoney(amount)}</span>
                  <span className="text-[8px] md:text-[9px] font-data text-zinc-600 w-8 md:w-10 text-right">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 50/30/20 Rule */}
      {totalIncome > 0 && (
        <div className="bg-zinc-900/20 border border-zinc-800/40 rounded-sm p-3 md:p-4" data-testid="budget-rule">
          <span className="text-[9px] md:text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block mb-2">50/30/20 Rule</span>
          <div className="grid grid-cols-3 gap-2 md:gap-3 text-center">
            <div>
              <span className="text-[8px] md:text-[9px] text-zinc-500 block">Needs (50%)</span>
              <span className="text-xs md:text-sm font-data font-bold text-zinc-300">{fmtMoney(totalIncome * 0.5)}</span>
            </div>
            <div>
              <span className="text-[8px] md:text-[9px] text-zinc-500 block">Wants (30%)</span>
              <span className="text-xs md:text-sm font-data font-bold text-zinc-300">{fmtMoney(totalIncome * 0.3)}</span>
            </div>
            <div>
              <span className="text-[8px] md:text-[9px] text-zinc-500 block">Savings (20%)</span>
              <span className="text-xs md:text-sm font-data font-bold text-emerald-400">{fmtMoney(totalIncome * 0.2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== NET WORTH (ASSETS & LIABILITIES) ========== */
function NetWorthTracker({ assets, setAssets, liabilities, setLiabilities, nextId, setNextId }) {
  const addAsset = () => {
    setAssets(prev => [...prev, { id: nextId, type: "Cash / Savings", name: "", value: 0 }]);
    setNextId(n => n + 1);
  };
  const addLiability = () => {
    setLiabilities(prev => [...prev, { id: nextId, type: "Credit Card", name: "", value: 0 }]);
    setNextId(n => n + 1);
  };

  const updateItem = (setter) => (id, field, val) => {
    setter(prev => prev.map(e => e.id === id ? { ...e, [field]: val } : e));
  };
  const removeItem = (setter) => (id) => {
    setter(prev => prev.filter(e => e.id !== id));
  };

  const totalAssets = useMemo(() => assets.reduce((s, a) => s + (a.value || 0), 0), [assets]);
  const totalLiabilities = useMemo(() => liabilities.reduce((s, l) => s + (l.value || 0), 0), [liabilities]);
  const netWorth = totalAssets - totalLiabilities;

  const assetBreakdown = useMemo(() => {
    const map = {};
    assets.forEach(a => { if (a.value > 0) map[a.type] = (map[a.type] || 0) + a.value; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [assets]);

  return (
    <div className="space-y-3 md:space-y-5" data-testid="net-worth-tracker">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 md:gap-3" data-testid="networth-summary">
        <SummaryCard label="Total Assets" value={fmtMoney(totalAssets)} icon={Wallet} color="text-emerald-400" />
        <SummaryCard label="Total Liabilities" value={fmtMoney(totalLiabilities)} icon={Receipt} color="text-red-400" />
        <SummaryCard
          label="Net Worth"
          value={`${netWorth >= 0 ? "" : "-"}${fmtMoney(netWorth)}`}
          icon={CurrencyDollar}
          color={netWorth >= 0 ? "text-amber-400" : "text-red-400"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5">
        {/* Assets */}
        <div className="bg-zinc-900/40 border border-amber-500/15 rounded-sm p-3 md:p-4" data-testid="assets-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wallet size={14} className="text-emerald-400" />
              <span className="text-[10px] md:text-[11px] font-semibold text-zinc-200 uppercase tracking-wider">Assets</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-emerald-900/40 text-emerald-400">{fmtMoney(totalAssets)}</Badge>
            </div>
            <button
              onClick={addAsset}
              data-testid="add-asset-btn"
              className="text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1 px-2 py-1 border border-emerald-800/40 rounded-sm hover:bg-emerald-900/20"
            >
              <Plus size={10} weight="bold" /> Add
            </button>
          </div>
          <div>
            {assets.map(a => (
              <div key={a.id} className="flex items-center gap-1.5 py-2 border-b border-zinc-800/30 last:border-0 group">
                <select value={a.type} onChange={e => updateItem(setAssets)(a.id, "type", e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-sm px-1.5 py-1.5 text-[10px] md:text-[11px] text-zinc-300 focus:outline-none focus:border-amber-500/50 w-24 md:w-36 shrink-0">
                  {ASSET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="text" value={a.name} onChange={e => updateItem(setAssets)(a.id, "name", e.target.value)} placeholder="Description" className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-[10px] md:text-[11px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50" />
                <div className="relative shrink-0">
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600">$</span>
                  <input type="number" value={a.value || ""} onChange={e => updateItem(setAssets)(a.id, "value", parseFloat(e.target.value) || 0)} placeholder="0" min="0" step="0.01" className="w-20 md:w-28 bg-zinc-950 border border-zinc-800 rounded-sm pl-4 pr-1 py-1.5 text-[10px] md:text-[11px] text-zinc-200 text-right font-data placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50" />
                </div>
                <button onClick={() => removeItem(setAssets)(a.id)} className="text-zinc-700 hover:text-red-400 transition-colors md:opacity-0 md:group-hover:opacity-100 shrink-0"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Liabilities */}
        <div className="bg-zinc-900/40 border border-amber-500/15 rounded-sm p-3 md:p-4" data-testid="liabilities-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Receipt size={14} className="text-red-400" />
              <span className="text-[10px] md:text-[11px] font-semibold text-zinc-200 uppercase tracking-wider">Liabilities</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-red-900/40 text-red-400">{fmtMoney(totalLiabilities)}</Badge>
            </div>
            <button
              onClick={addLiability}
              data-testid="add-liability-btn"
              className="text-[10px] text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 px-2 py-1 border border-red-800/40 rounded-sm hover:bg-red-900/20"
            >
              <Plus size={10} weight="bold" /> Add
            </button>
          </div>
          <div>
            {liabilities.map(l => (
              <div key={l.id} className="flex items-center gap-1.5 py-2 border-b border-zinc-800/30 last:border-0 group">
                <select value={l.type} onChange={e => updateItem(setLiabilities)(l.id, "type", e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-sm px-1.5 py-1.5 text-[10px] md:text-[11px] text-zinc-300 focus:outline-none focus:border-amber-500/50 w-24 md:w-36 shrink-0">
                  {LIABILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="text" value={l.name} onChange={e => updateItem(setLiabilities)(l.id, "name", e.target.value)} placeholder="Description" className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-[10px] md:text-[11px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50" />
                <div className="relative shrink-0">
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600">$</span>
                  <input type="number" value={l.value || ""} onChange={e => updateItem(setLiabilities)(l.id, "value", parseFloat(e.target.value) || 0)} placeholder="0" min="0" step="0.01" className="w-20 md:w-28 bg-zinc-950 border border-zinc-800 rounded-sm pl-4 pr-1 py-1.5 text-[10px] md:text-[11px] text-zinc-200 text-right font-data placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50" />
                </div>
                <button onClick={() => removeItem(setLiabilities)(l.id)} className="text-zinc-700 hover:text-red-400 transition-colors md:opacity-0 md:group-hover:opacity-100 shrink-0"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Asset Allocation Breakdown */}
      {assetBreakdown.length > 0 && (
        <div className="bg-zinc-900/30 border border-amber-500/10 rounded-sm p-3 md:p-4" data-testid="asset-breakdown">
          <span className="text-[9px] md:text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block mb-2 md:mb-3">Asset Allocation</span>
          <div className="space-y-1.5 md:space-y-2">
            {assetBreakdown.map(([type, val]) => {
              const pct = totalAssets > 0 ? (val / totalAssets) * 100 : 0;
              return (
                <div key={type} className="flex items-center gap-2 md:gap-3">
                  <span className="text-[10px] md:text-[11px] text-zinc-400 w-24 md:w-40 shrink-0 truncate">{type}</span>
                  <div className="flex-1 h-2.5 md:h-3 bg-zinc-800/60 rounded-sm overflow-hidden">
                    <div className="h-full bg-emerald-500/40 rounded-sm transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[9px] md:text-[10px] font-data text-zinc-300 w-16 md:w-24 text-right">{fmtMoney(val)}</span>
                  <span className="text-[8px] md:text-[9px] font-data text-zinc-600 w-8 md:w-10 text-right">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== HISTORY / COMPARISON TAB ========== */
function HistoryTab({ snapshots, onDelete, onLoad }) {
  if (!snapshots.length) {
    return (
      <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm p-8 text-center" data-testid="history-empty">
        <ClockCounterClockwise size={28} weight="duotone" className="text-zinc-600 mx-auto mb-2" />
        <p className="text-xs text-zinc-400 mb-1">No saved snapshots yet</p>
        <p className="text-[10px] text-zinc-600 leading-relaxed max-w-sm mx-auto">
          Fill in your budget or net worth, then click <strong className="text-amber-400">Save Snapshot</strong> to start tracking month-over-month progress.
        </p>
      </div>
    );
  }

  const latest = snapshots[0];
  const prev = snapshots[1];

  const delta = (curr, prevVal) => {
    if (prevVal == null || prevVal === 0) return { val: null, pct: null };
    const diff = curr - prevVal;
    const pct = ((diff / Math.abs(prevVal)) * 100);
    return { val: diff, pct };
  };

  const deltaInc = prev ? delta(latest.totals.income || 0, prev.totals.income || 0) : null;
  const deltaExp = prev ? delta(latest.totals.expenses || 0, prev.totals.expenses || 0) : null;
  const deltaNet = prev ? delta(latest.totals.net || 0, prev.totals.net || 0) : null;
  const deltaNW  = prev ? delta(latest.totals.net_worth || 0, prev.totals.net_worth || 0) : null;

  const DeltaPill = ({ d, inverted = false }) => {
    if (!d || d.val == null) return <span className="text-[9px] text-zinc-600">—</span>;
    const up = d.val > 0;
    const good = inverted ? !up : up;
    const color = d.val === 0 ? "text-zinc-500" : good ? "text-emerald-400" : "text-red-400";
    const Arrow = d.val === 0 ? Minus : up ? ArrowUp : ArrowDown;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[9px] md:text-[10px] font-data ${color}`}>
        <Arrow size={9} weight="bold" /> {fmtMoney(d.val)} ({d.pct > 0 ? "+" : ""}{d.pct.toFixed(1)}%)
      </span>
    );
  };

  return (
    <div className="space-y-3 md:space-y-5" data-testid="history-tab">
      {prev && (
        <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-3 md:p-4" data-testid="mom-comparison">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <TrendUp size={14} className="text-amber-400" />
            <span className="text-[10px] md:text-[11px] font-semibold text-zinc-200 uppercase tracking-wider">Progress</span>
            <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-900/40 text-amber-300">{prev.label} → {latest.label}</Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            <div className="bg-zinc-950/50 border border-emerald-500/15 rounded-sm p-2 md:p-3">
              <span className="text-[8px] md:text-[9px] uppercase text-zinc-500 block">Income</span>
              <p className="text-xs md:text-sm font-data font-bold text-zinc-200">{fmtMoney(latest.totals.income || 0)}</p>
              <DeltaPill d={deltaInc} />
            </div>
            <div className="bg-zinc-950/50 border border-red-500/15 rounded-sm p-2 md:p-3">
              <span className="text-[8px] md:text-[9px] uppercase text-zinc-500 block">Expenses</span>
              <p className="text-xs md:text-sm font-data font-bold text-zinc-200">{fmtMoney(latest.totals.expenses || 0)}</p>
              <DeltaPill d={deltaExp} inverted />
            </div>
            <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-2 md:p-3">
              <span className="text-[8px] md:text-[9px] uppercase text-zinc-500 block">Net Cash Flow</span>
              <p className={`text-xs md:text-sm font-data font-bold ${(latest.totals.net || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtMoney(latest.totals.net || 0)}</p>
              <DeltaPill d={deltaNet} />
            </div>
            <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-2 md:p-3">
              <span className="text-[8px] md:text-[9px] uppercase text-zinc-500 block">Net Worth</span>
              <p className={`text-xs md:text-sm font-data font-bold ${(latest.totals.net_worth || 0) >= 0 ? "text-amber-400" : "text-red-400"}`}>{fmtMoney(latest.totals.net_worth || 0)}</p>
              <DeltaPill d={deltaNW} />
            </div>
          </div>
        </div>
      )}

      <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm overflow-hidden" data-testid="snapshots-table">
        <div className="px-3 md:px-4 py-2 border-b border-zinc-800/60 bg-zinc-900/50">
          <span className="text-[10px] md:text-[11px] font-semibold text-zinc-200 uppercase tracking-wider">All Snapshots ({snapshots.length})</span>
        </div>
        <div className="divide-y divide-zinc-800/40">
          {snapshots.map((s, idx) => {
            const prevS = snapshots[idx + 1];
            const mNet = prevS ? delta(s.totals.net || 0, prevS.totals.net || 0) : null;
            return (
              <div key={s.id} className="px-3 md:px-4 py-2.5 md:py-3 hover:bg-zinc-900/40 transition-colors" data-testid={`snapshot-row-${idx}`}>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[11px] md:text-xs font-semibold text-zinc-100">{s.label}</span>
                  {idx === 0 && <Badge className="text-[7px] px-1 py-0 h-3.5 border-0 rounded-sm bg-amber-500/20 text-amber-300">LATEST</Badge>}
                  <span className="text-[9px] text-zinc-600 ml-auto">{new Date(s.created_at).toLocaleDateString()}</span>
                  <button onClick={() => onLoad(s)} data-testid={`load-snap-${idx}`} className="text-[9px] text-zinc-500 hover:text-amber-300 transition-colors px-1.5 py-0.5 rounded-sm border border-zinc-800 hover:border-amber-500/40">Load</button>
                  <button onClick={() => onDelete(s.id)} data-testid={`delete-snap-${idx}`} className="text-zinc-600 hover:text-red-400 transition-colors p-0.5"><Trash size={11} /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 md:gap-3 text-[9px] md:text-[10px] font-data">
                  <div><span className="text-zinc-500">Income:</span> <span className="text-emerald-400">{fmtMoney(s.totals.income || 0)}</span></div>
                  <div><span className="text-zinc-500">Expenses:</span> <span className="text-red-400">{fmtMoney(s.totals.expenses || 0)}</span></div>
                  <div><span className="text-zinc-500">Net:</span> <span className={(s.totals.net || 0) >= 0 ? "text-emerald-400" : "text-red-400"}>{fmtMoney(s.totals.net || 0)}</span> {mNet && mNet.val != null && (<span className={`ml-1 ${mNet.val >= 0 ? "text-emerald-500/70" : "text-red-500/70"}`}>({mNet.pct > 0 ? "+" : ""}{mNet.pct.toFixed(1)}%)</span>)}</div>
                  <div><span className="text-zinc-500">Net Worth:</span> <span className="text-amber-400">{fmtMoney(s.totals.net_worth || 0)}</span></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ========== MAIN BUDGETING PAGE ========== */
export default function Budgeting() {
  const { user } = useAuth();
  const [view, setView] = useState("budget");

  // Budget state (lifted from BudgetCalculator)
  const [incomes, setIncomes] = useState([{ id: 1, category: "Salary", name: "Monthly Salary", amount: 0 }]);
  const [expenses, setExpenses] = useState([
    { id: 1, category: "Housing", name: "Rent / Mortgage", amount: 0 },
    { id: 2, category: "Utilities", name: "Electric / Water / Gas", amount: 0 },
    { id: 3, category: "Food", name: "Groceries", amount: 0 },
    { id: 4, category: "Transportation", name: "Car / Gas / Transit", amount: 0 },
  ]);
  const [nextIdBudget, setNextIdBudget] = useState(10);

  // Net worth state (lifted from NetWorthTracker)
  const [assets, setAssets] = useState([
    { id: 1, type: "Cash / Savings", name: "Emergency Fund", value: 0 },
    { id: 2, type: "Stocks / ETFs", name: "Brokerage Account", value: 0 },
    { id: 3, type: "Crypto", name: "Crypto Portfolio", value: 0 },
  ]);
  const [liabilities, setLiabilities] = useState([{ id: 1, type: "Credit Card", name: "Credit Card Balance", value: 0 }]);
  const [nextIdNW, setNextIdNW] = useState(10);

  // Snapshot history
  const [snapshots, setSnapshots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const fetchSnapshots = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/budget/snapshots`);
      setSnapshots(r.data.snapshots || []);
    } catch {
      setSnapshots([]);
    }
  }, [user]);

  useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);

  const defaultLabel = () => {
    const d = new Date();
    return d.toLocaleString("en-US", { month: "short", year: "numeric" });
  };

  const saveSnapshot = async () => {
    if (!user) return;
    const suggested = defaultLabel();
    // eslint-disable-next-line no-alert
    const label = window.prompt("Label this snapshot (e.g. 'Feb 2026', 'Post-Bonus'):", suggested);
    if (!label || !label.trim()) return;

    const totalIncome = incomes.reduce((s, e) => s + (e.amount || 0), 0);
    const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    const totalAssets = assets.reduce((s, a) => s + (a.value || 0), 0);
    const totalLiabilities = liabilities.reduce((s, l) => s + (l.value || 0), 0);

    setSaving(true);
    try {
      await axios.post(`${API}/budget/snapshot`, {
        label: label.trim(),
        incomes, expenses, assets, liabilities,
        totals: {
          income: totalIncome,
          expenses: totalExpenses,
          net: totalIncome - totalExpenses,
          assets: totalAssets,
          liabilities: totalLiabilities,
          net_worth: totalAssets - totalLiabilities,
          savings_rate: totalIncome > 0 ? ((totalIncome - totalExpenses) / totalIncome) * 100 : 0,
        },
      });
      setSaveMsg("Snapshot saved!");
      setTimeout(() => setSaveMsg(""), 2500);
      await fetchSnapshots();
    } catch (err) {
      setSaveMsg(err.response?.data?.detail || "Failed to save");
      setTimeout(() => setSaveMsg(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try {
      await axios.delete(`${API}/budget/snapshot/${id}`);
      await fetchSnapshots();
    } catch {}
  };

  const loadSnapshot = (snap) => {
    if (!window.confirm(`Load snapshot "${snap.label}" into the editor? Your current unsaved inputs will be replaced.`)) return;
    if (snap.incomes?.length) setIncomes(snap.incomes);
    if (snap.expenses?.length) setExpenses(snap.expenses);
    if (snap.assets?.length) setAssets(snap.assets);
    if (snap.liabilities?.length) setLiabilities(snap.liabilities);
    setView("budget");
  };

  return (
    <div className="p-3 md:p-5 max-w-[1400px] space-y-3 md:space-y-5" data-testid="budgeting-page">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-heading text-lg md:text-xl font-bold text-zinc-50 tracking-tight flex items-center gap-2">
            <Calculator size={18} weight="bold" className="text-amber-500" />
            Budgeting
          </h2>
          <p className="text-[10px] md:text-xs text-zinc-500 mt-0.5 hidden sm:block">Track income, expenses, assets, and liabilities over time</p>
        </div>
        {user ? (
          <div className="flex items-center gap-2 shrink-0">
            {saveMsg && (
              <span className="text-[10px] text-emerald-400 hidden md:flex items-center gap-1" data-testid="save-msg">
                <CheckCircle size={11} weight="fill" /> {saveMsg}
              </span>
            )}
            <button
              onClick={saveSnapshot}
              disabled={saving}
              data-testid="save-snapshot-btn"
              className="flex items-center gap-1.5 text-[10px] md:text-[11px] px-2.5 md:px-3 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 rounded-sm border border-amber-500/40 transition-colors disabled:opacity-40 whitespace-nowrap"
            >
              <FloppyDisk size={12} weight="bold" />
              <span className="hidden sm:inline">{saving ? "Saving..." : "Save Snapshot"}</span>
              <span className="sm:hidden">Save</span>
            </button>
          </div>
        ) : (
          <span className="text-[9px] md:text-[10px] text-zinc-500 italic shrink-0">Sign in to save</span>
        )}
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="h-8 bg-zinc-900/60 border border-amber-500/20 p-0.5 gap-0.5 overflow-x-auto max-w-full flex" data-testid="budget-view-tabs">
          <TabsTrigger value="budget" data-testid="budget-tab-calculator" className="text-[10px] px-3 py-1 h-7 gap-1.5 font-medium whitespace-nowrap data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300">
            <Calculator size={11} weight="bold" />Budget Calculator
          </TabsTrigger>
          <TabsTrigger value="networth" data-testid="budget-tab-networth" className="text-[10px] px-3 py-1 h-7 gap-1.5 font-medium whitespace-nowrap data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300">
            <Wallet size={11} weight="bold" />Assets & Net Worth
          </TabsTrigger>
          {user && (
            <TabsTrigger value="history" data-testid="budget-tab-history" className="text-[10px] px-3 py-1 h-7 gap-1.5 font-medium whitespace-nowrap data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-300">
              <ClockCounterClockwise size={11} weight="bold" />History{snapshots.length > 0 && <span className="text-[8px] text-zinc-500 ml-0.5">({snapshots.length})</span>}
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {view === "budget" && <BudgetCalculator incomes={incomes} setIncomes={setIncomes} expenses={expenses} setExpenses={setExpenses} nextId={nextIdBudget} setNextId={setNextIdBudget} />}
      {view === "networth" && <NetWorthTracker assets={assets} setAssets={setAssets} liabilities={liabilities} setLiabilities={setLiabilities} nextId={nextIdNW} setNextId={setNextIdNW} />}
      {view === "history" && <HistoryTab snapshots={snapshots} onDelete={deleteSnapshot} onLoad={loadSnapshot} />}

      <p className="text-[9px] text-zinc-700 text-center pt-2">
        {user
          ? "Snapshots are saved to your account. Current-session edits remain local until you save."
          : "All data is calculated locally in your browser. Sign in to save monthly snapshots and track progress."}
      </p>
    </div>
  );
}
